import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { modelsAPI } from '../api/models';
import { 
  ArrowLeft, 
  Eye, 
  Check, 
  ArrowRight,
  Clock,
  Users,
  Shield,
  Smartphone,
  Mail,
  Phone,
  Sparkles
} from 'lucide-react';
import Loader from '../components/common/Loader';

export default function ModelDetail() {
  const { id } = useParams();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedModels, setRelatedModels] = useState([]);
  const [activeTab, setActiveTab] = useState('features');

  const loadModel = useCallback(async () => {
    try {
      const response = await modelsAPI.getById(id);
      setModel(response.data);
      
      // Charger les modèles similaires
      const related = await modelsAPI.getAll(response.data.category);
      setRelatedModels(related.data.filter(m => m._id !== id));
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadModel();
  }, [loadModel]);

  if (loading) return <Loader />;
  if (!model) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-white mb-4">Modèle non trouvé</h1>
        <Link to="/models" className="text-purple-400 hover:text-purple-300">
          ← Retour aux modèles
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/10 to-pink-900/20" />
        
        <div className="relative container mx-auto px-4">
          {/* Back Button */}
          <Link 
            to="/models" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour aux modèles
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              {/* Category & Price */}
              <div className="flex flex-wrap items-center gap-4">
                <span className="px-4 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full text-purple-300 font-semibold border border-purple-500/30">
                  {model.category}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-bold text-white">{model.price.base}€</span>
                  {model.price.custom && (
                    <span className="text-sm text-gray-400">
                      (Personnalisable: {model.price.custom}€)
                    </span>
                  )}
                </div>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                {model.title}
              </h1>

              {/* Description */}
              <p className="text-xl text-gray-300 leading-relaxed">
                {model.description}
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <span className="text-sm text-gray-400">Délai</span>
                  </div>
                  <span className="text-lg font-bold text-white">7-14 jours</span>
                </div>
                
                <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-green-400" />
                    <span className="text-sm text-gray-400">Garantie</span>
                  </div>
                  <span className="text-lg font-bold text-white">12 mois</span>
                </div>
                
                <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="w-5 h-5 text-purple-400" />
                    <span className="text-sm text-gray-400">Responsive</span>
                  </div>
                  <span className="text-lg font-bold text-white">Oui</span>
                </div>
                
                <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm text-gray-400">Support</span>
                  </div>
                  <span className="text-lg font-bold text-white">6 mois</span>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Preview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              {model.previewImage ? (
                <div className="rounded-2xl overflow-hidden border-2 border-gray-700">
                  <img 
                    src={model.previewImage} 
                    alt={model.title}
                    className="w-full h-auto"
                  />
                </div>
              ) : (
                <div className="h-96 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-dashed border-gray-700 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl mb-4 opacity-30">
                      {model.category === 'ecommerce' ? '🛒' : 
                       model.category === 'portfolio' ? '🎨' : '🌐'}
                    </div>
                    <p className="text-gray-500">Aperçu du modèle</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features & Details Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {/* Tabs */}
          <div className="flex border-b border-gray-800 mb-8">
            {['features', 'tech', 'support'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 font-semibold transition-colors ${
                  activeTab === tab
                    ? 'text-purple-400 border-b-2 border-purple-400'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {tab === 'features' && 'Fonctionnalités'}
                {tab === 'tech' && 'Technologies'}
                {tab === 'support' && 'Support'}
              </button>
            ))}
          </div>

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {model.features?.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-6 bg-gray-800/30 rounded-xl border border-gray-700 hover:border-purple-500/50 transition-colors"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-400" />
                    </div>
                    <span className="text-lg font-semibold text-white">
                      Fonctionnalité {idx + 1}
                    </span>
                  </div>
                  <p className="text-gray-300">{feature}</p>
                </motion.div>
              ))}
            </div>
          )}

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-16 p-8 md:p-12 bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl border border-gray-700"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Intéressé par ce modèle ?
                </h2>
                <p className="text-gray-300 mb-6">
                  Ce modèle peut être entièrement personnalisé selon vos besoins spécifiques.
                  Contactez-nous pour obtenir un devis personnalisé.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                  >
                    Demander un devis
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  
                  {model.demoUrl && (
                    <a
                      href={model.demoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-700 text-white font-semibold rounded-xl hover:bg-gray-800/50 transition-colors"
                    >
                      <Eye className="w-5 h-5" />
                      Voir la démo
                    </a>
                  )}
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-purple-400" />
                  <span className="text-gray-300">contact@devtech.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-purple-400" />
                  <span className="text-gray-300">+33 1 23 45 67 89</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Models */}
      {relatedModels.length > 0 && (
        <section className="py-20 border-t border-gray-800">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Modèles similaires
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Découvrez d'autres modèles dans la même catégorie
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {relatedModels.slice(0, 3).map((related) => (
                <Link
                  key={related._id}
                  to={`/models/${related._id}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden rounded-2xl border border-gray-700 hover:border-purple-500/50 transition-colors">
                    {/* Preview */}
                    <div className="h-48 bg-gradient-to-br from-gray-800 to-gray-900">
                      {related.previewImage ? (
                        <img 
                          src={related.previewImage} 
                          alt={related.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-4xl opacity-30">
                            {related.category === 'ecommerce' ? '🛒' : '🌐'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-lg font-bold text-white group-hover:text-purple-300 transition-colors">
                          {related.title}
                        </h3>
                        <span className="text-lg font-bold text-white">
                          {related.price.base}€
                        </span>
                      </div>
                      
                      <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                        {related.description.substring(0, 100)}...
                      </p>
                      
                      <div className="flex justify-between items-center">
                        <span className="px-3 py-1 bg-gray-800/50 rounded-lg text-sm text-gray-400">
                          {related.category}
                        </span>
                        <span className="text-sm text-purple-400 group-hover:text-purple-300 transition-colors">
                          Voir détails →
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center p-8 md:p-12 bg-gradient-to-br from-purple-900/20 via-blue-900/10 to-pink-900/20 rounded-2xl border border-purple-500/30">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-semibold text-purple-300">À VOTRE SERVICE</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à démarrer votre projet ?
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Contactez-nous dès aujourd'hui pour discuter de vos besoins
              et obtenir un devis personnalisé.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-2xl hover:shadow-purple-500/25 transition-all"
              >
                Demander un devis gratuit
              </Link>
              
              <Link
                to="/models"
                className="px-8 py-4 border-2 border-gray-700 text-white font-semibold rounded-xl hover:bg-gray-800/50 transition-colors"
              >
                Voir tous les modèles
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}